/**
 * 酒店相关接口
 * 对应: api-home-hotel.md, api-home-hotel-order.md
 */

import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 酒店列表项 */
export interface HotelListItem {
  id: number;
  hotel_name: string;
  cover_image: string;
  star_rating: number;
  star_rating_text: string;
  city: string;
  district: string;
  address: string;
  longitude: number;
  latitude: number;
  distance: number | null;
  distance_text: string | null;
  contact_phone: string;
  min_price: number;
  room_cover: string;
  room_count: number;
  is_recommend: number;
  is_hot: number;
  comment_count: number;
  avg_product_score: number;
  avg_service_score: number;
}

/** 酒店列表响应 */
export interface HotelListResponse {
  code: number;
  message: string;
  data: {
    list: HotelListItem[];
    total: number;
    page: number;
    limit: number;
  };
}

/** 酒店详情 - 房型信息 */
export interface HotelRoomItem {
  id: number;
  room_name: string;
  cover_image: string;
  images: string[];
  bed_type: number;
  bed_type_text: string;
  max_guest: number;
  area: number;
  window: number;
  window_text: string;
  smoke: number;
  smoke_text: string;
  breakfast: number;
  breakfast_text: string;
  price: number;
  weekend_price: number;
  holiday_price: number;
  market_price: number;
  stock: number;
  facilities: string[];
  intro: string;
}

/** 评论项 */
export interface HotelComment {
  id: number;
  uid: number;
  nickname: string;
  avatar: string;
  product_score: number;
  service_score: number;
  comment: string;
  pics: string[];
  add_time: number;
  add_time_text: string;
  is_reply: number;
  merchant_reply_content: string | null;
  merchant_reply_time: string | null;
}

/** 酒店详情 */
export interface HotelDetail extends Omit<HotelListItem, "distance" | "distance_text" | "min_price" | "room_cover"> {
  images: string[];
  province: string;
  full_address: string;
  hotel_intro: string;
  facilities: string[];
  mer_id: number;
  mer_name: string;
  rooms: HotelRoomItem[];
  comment_stats: {
    comment_count: number;
    avg_product_score: number;
    avg_service_score: number;
  };
  comment_list: HotelComment[];
}

/** 酒店详情响应 */
export interface HotelDetailResponse {
  code: number;
  message: string;
  data: HotelDetail;
}

/** 房型详情 */
export interface RoomDetail {
  id: number;
  hotel_id: number;
  hotel_name: string;
  room_name: string;
  cover_image: string;
  images: string[];
  bed_type: number;
  bed_type_text: string;
  max_guest: number;
  area: number;
  floor: string;
  window: number;
  window_text: string;
  smoke: number;
  smoke_text: string;
  breakfast: number;
  breakfast_text: string;
  price: number;
  weekend_price: number;
  holiday_price: number;
  market_price: number;
  stock: number;
  facilities: string[];
  intro: string;
  min_nights: number;
  max_nights: number;
  advance_book_days: number;
  cancel_policy: Array<{ day: number; text: string }>;
  comment_stats: {
    comment_count: number;
    avg_product_score: number;
    avg_service_score: number;
  };
  comment_list: HotelComment[];
}

/** 房型详情响应 */
export interface RoomDetailResponse {
  code: number;
  message: string;
  data: RoomDetail;
}

// ==================== 订单类型 ====================

/** 每晚价格 */
export interface NightPrice {
  date: string;
  price: number;
  is_weekend: boolean;
  is_holiday: boolean;
}

/** 取消政策 */
export interface CancelPolicy {
  day: number;
  text: string;
}

/** getRooms 接口返回的房型项（按日期查价） */
export interface HotelRoomItem2 {
  id: number;
  room_name: string;
  cover_image: string;
  images: string[];
  bed_type: number;
  bed_type_text: string;
  max_guest: number;
  area: number;
  window: number;
  window_text: string;
  breakfast: number;
  breakfast_text: string;
  price: number;
  weekend_price: number | null;
  holiday_price: number | null;
  stock: number | null;
  facilities: string;
  intro: string;
}

/** getRooms 接口响应 */
export interface HotelRoomsResponse {
  code: number;
  message: string;
  data: HotelRoomItem2[];
}

/** getPrice 接口响应 */
export interface HotelPriceResponse {
  code: number;
  message: string;
  data: {
    total_price: number;
  };
}

/** 预创建订单数据 */
export interface PreCreateOrderData {
  order_id: number;
  order_no: string;
  hotel: {
    id: number;
    name: string;
    cover: string;
    star: number;
    star_text: string;
    address: string;
    contact_phone: string;
    facilities?: string[];   // 酒店设施列表
  };
  room: {
    id: number;
    name: string;
    cover: string;
    bed_type: number;
    bed_type_text: string;
    max_guest: number;
  };
  check_in_date: string;
  check_out_date: string;
  check_in_time: string;
  check_out_time: string;
  nights: number;
  night_prices: NightPrice[];
  total_amount: number;
  cancel_policy: CancelPolicy[];
}

/** 确认订单数据 */
export interface ConfirmOrderData {
  order_id: number;
  order_no: string;
  total_amount: number;
}

/** 订单详情数据 */
export interface OrderDetailData {
  id: number;
  order_no: string;
  has_comment: number; // 1=已评论 0=未评论
  hotel: PreCreateOrderData["hotel"];
  room: PreCreateOrderData["room"];
  check_in_date: string;
  check_out_date: string;
  check_in_time: string;
  check_out_time: string;
  stay_period: string;
  nights: number;
  guest_name: string;
  guest_phone: string;
  guest_count: number;
  special_requests: string[];
  room_price: number;
  night_prices: NightPrice[];
  room_amount: number;
  total_amount: number;
  cancel_policy: CancelPolicy[];
  refund_amount: number;
  refund_reason: string;
  refund_time: string | null;
  status: number;
  status_text: string;
  pay_type: number;
  pay_type_text: string;
  pay_time: string;
  transaction_id: string;
  add_time: string;
  update_time: string;
  refund_info?: {
    has_refund: number;
    refund_id: number;
    refund_no: string;
    refund_status: number;
    refund_status_text: string;
    apply_desc: string;
    apply_img: string[];
    apply_reason: string;
    create_time: string;
    refund_money: string;
    refuse_desc: string;
  };
}

/** 订单列表项 */
export interface OrderListItem {
  id: number;
  order_no: string;
  hotel_name: string;
  hotel_cover: string;
  room_name: string;
  room_cover: string;
  check_in_date: string;
  check_out_date: string;
  nights: number;
  total_amount: string;
  status: number;
  status_text: string;
  add_time: string;
}

// ==================== 接口请求 ====================

/** 酒店列表搜索参数 */
export interface HotelListParams {
  page?: number;
  limit?: number;
  city?: string;
  keyword?: string;
  star_rating?: string; // 多个逗号分隔, 如 "4,5"
  min_price?: number;
  max_price?: number;
  longitude?: number | string;
  latitude?: number | string;
  radius?: number;
  sort?: "distance" | "price" | "price_desc" | "recommend";
}

/** 获取酒店列表 */
export function getHotelList(params: HotelListParams = {}) {
  return http.get<HotelListResponse>("/home/hotel/list", { params });
}

/** 获取酒店详情 */
export function getHotelDetail(id: number) {
  return http.get<HotelDetailResponse>("/home/hotel/detail", { params: { id } });
}

/** 获取房型详情 */
export function getRoomDetail(id: number) {
  return http.get<RoomDetailResponse>("/home/hotel/room-detail", { params: { id } });
}

/** 根据入住/离店日期获取酒店房型列表（含当日价格与库存） */
export function getHotelRooms(params: {
  hotel_id: number | string;
  start_date: string;
  end_date: string;
}) {
  return http.get<HotelRoomsResponse>("/home/hotel/getRooms", { params });
}

/** 按酒店/日期/房型查询总价（含周末/节假日/折扣等真实价格） */
export function getHotelPrice(params: {
  hotel_id: number | string;
  start_date: string;
  end_date: string;
  room_id: number | string;
}) {
  return http.get<HotelPriceResponse>("/home/hotel/getPrice", { params });
}

/** 预创建订单 */
export function preCreateHotelOrder(data: {
  room_id: number;
  check_in_date: string;
  check_out_date: string;
  guest_count?: number;
  /** 酒店ID（部分后端实现依赖它反查价格，缺省可能报"价格异常"） */
  hotel_id?: number;
}) {
  return http.post<{ code: number; message: string; data: PreCreateOrderData }>("/home/hotel-order/pre-create", data);
}

/** 确认订单 - 入住人信息 */
export interface HotelConfirmTraveler {
  name: string;
  type_name?: string;       // 成人/儿童(6-12岁)/幼儿(<6岁)
  id_card?: string;
  phone?: string;
  id_card_front?: string;
  id_card_back?: string;
}

/** 确认订单 */
export function confirmHotelOrder(data: {
  order_no: string;
  guest_name: string;
  guest_phone: string;
  guest_email: string;
  travelers: HotelConfirmTraveler[];
  special_requests?: string[];
}) {
  return http.post<{ code: number; message: string; data: ConfirmOrderData }>("/home/hotel-order/confirm", data);
}

/** 获取订单详情 */
export function getHotelOrderDetail(id: number) {
  return http.get<{ code: number; message: string; data: OrderDetailData }>("/home/hotel-order/detail", { params: { id } });
}

/** 取消订单 */
export function cancelHotelOrder(data: { id: number; reason?: string }) {
  return http.post<{ code: number; message: string; data: { order_no: string; refund_amount: number; status: number; status_text: string } }>("/home/hotel-order/cancel", data);
}

/** 获取订单列表 */
export function getHotelOrderList(params: { page?: number; limit?: number; status?: number } = {}) {
  return http.get<{ code: number; message: string; data: { list: OrderListItem[]; total: number; page: number; limit: number } }>("/home/hotel-order/list", { params });
}
